import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  findMatchingPreset,
  formatWindowRange,
  getWindowKey,
  type DeliveryWindowPreset,
  type WindowLike,
} from "~/shared/utils/delivery-window";

type WindowPresetSelectProps = Readonly<{
  /** Current window; may be a preset or a custom window */
  value: WindowLike | null;
  presets: readonly DeliveryWindowPreset[];
  onSelect: (preset: DeliveryWindowPreset) => void;
  /** "cell": compact text trigger for the orders grid. "field": full-width input-style trigger for side panels. */
  variant: "cell" | "field";
  disabled?: boolean;
  /** "field" only: text shown when the value matches no preset (e.g. a custom window is chosen) */
  placeholder?: string;
  ariaLabel?: string;
}>;

const POPOVER_WIDTH = 300;
const POPOVER_MAX_HEIGHT = 380;
const POPOVER_GAP = 4;

const GROUP_TITLES: Record<DeliveryWindowPreset["group"], string> = {
  standard: "Standard",
  location: "Location windows",
};

/**
 * Dropdown of delivery window presets (standard windows, then REF-0007 location windows).
 * The list is rendered in a portal with fixed positioning so it is not clipped by the scrolling table.
 */
export function WindowPresetSelect({
  value,
  presets,
  onSelect,
  variant,
  disabled,
  placeholder = "Select a window",
  ariaLabel = "Delivery window",
}: WindowPresetSelectProps) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  const valueKey = getWindowKey(value);
  const matchedPreset = useMemo(() => findMatchingPreset(presets, value), [presets, value]);
  const groups = useMemo(
    () =>
      (["standard", "location"] as const)
        .map((group) => ({ group, items: presets.filter((p) => p.group === group) }))
        .filter((g) => g.items.length > 0),
    [presets]
  );

  const close = useCallback((restoreFocus: boolean) => {
    setOpen(false);
    if (restoreFocus) triggerRef.current?.focus();
  }, []);

  const updatePosition = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const spaceBelow = window.innerHeight - rect.bottom;
    const top =
      spaceBelow > POPOVER_MAX_HEIGHT + POPOVER_GAP
        ? rect.bottom + POPOVER_GAP
        : Math.max(8, rect.top - POPOVER_MAX_HEIGHT - POPOVER_GAP);
    const left = Math.max(8, Math.min(window.innerWidth - POPOVER_WIDTH - 8, rect.left));
    setPosition({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (open) updatePosition();
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    function onDocMouseDown(e: MouseEvent) {
      const target = e.target as Node;
      const insidePopover = popoverRef.current?.contains(target) ?? false;
      const onTrigger = triggerRef.current?.contains(target) ?? false;
      if (insidePopover || onTrigger) return;
      close(false);
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") close(true);
    }
    // The trigger moves with the table/panel scroll; close rather than leave the list detached.
    function onScroll(e: Event) {
      if (popoverRef.current?.contains(e.target as Node)) return;
      close(false);
    }
    document.addEventListener("mousedown", onDocMouseDown, true);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown, true);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open, close, updatePosition]);

  // Focus the selected (or first) option when the list opens
  useEffect(() => {
    if (!open || !position) return;
    const items = popoverRef.current?.querySelectorAll<HTMLButtonElement>("[data-preset-option]");
    if (!items?.length) return;
    const selected = Array.from(items).find((el) => el.dataset.presetKey === valueKey);
    (selected ?? items[0]).focus();
  }, [open, position, valueKey]);

  // Arrow keys move between options (handled on the option buttons themselves)
  function onOptionKeyDown(e: React.KeyboardEvent) {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    e.preventDefault();
    const items = Array.from(
      popoverRef.current?.querySelectorAll<HTMLButtonElement>("[data-preset-option]") ?? []
    );
    const index = items.indexOf(document.activeElement as HTMLButtonElement);
    const next = e.key === "ArrowDown" ? Math.min(items.length - 1, index + 1) : Math.max(0, index - 1);
    items[next]?.focus();
  }

  function pick(preset: DeliveryWindowPreset) {
    close(true);
    if (getWindowKey(preset) !== valueKey) onSelect(preset);
  }

  const cellText = value ? formatWindowRange(value) : "";
  // Every window is shown as a time range; the Morning/Midday/Evening names are not shown on this screen
  const fieldText = matchedPreset ? formatWindowRange(matchedPreset) : "";

  const trigger =
    variant === "cell" ? (
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={ariaLabel}
        title={disabled ? undefined : "Change delivery window"}
        onClick={() => setOpen((v) => !v)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "2px 4px",
          margin: "-2px -4px",
          border: "1px solid transparent",
          borderRadius: 4,
          background: "transparent",
          font: "inherit",
          fontWeight: 600,
          color: "inherit",
          whiteSpace: "nowrap",
          cursor: disabled ? "default" : "pointer",
        }}
      >
        <span>{cellText || "--"}</span>
        {!disabled && (
          <span
            aria-hidden
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 20,
              height: 20,
              border: "1px solid var(--border)",
              borderRadius: 4,
              background: "white",
              color: "var(--primary-blue)",
              fontSize: 10,
              lineHeight: 1,
            }}
          >
            ▼
          </span>
        )}
      </button>
    ) : (
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => setOpen((v) => !v)}
        style={{
          position: "relative",
          width: "100%",
          textAlign: "left",
          padding: "10px 36px 10px 12px",
          fontSize: 14,
          borderRadius: 6,
          border: "1px solid var(--border)",
          background: "white",
          color: "var(--text-dark)",
          ...(disabled && { opacity: 0.7, cursor: "not-allowed" }),
        }}
      >
        {fieldText || <span style={{ color: "var(--text-muted)" }}>{placeholder}</span>}
        <span
          aria-hidden
          style={{
            position: "absolute",
            right: 12,
            top: "50%",
            transform: open ? "translateY(-50%) rotate(180deg)" : "translateY(-50%)",
            pointerEvents: "none",
            fontSize: 12,
            color: "var(--text-muted)",
            transition: "transform 0.15s ease",
          }}
        >
          ▼
        </span>
      </button>
    );

  return (
    <>
      {trigger}
      {open &&
        !disabled &&
        position &&
        createPortal(
          <div
            ref={popoverRef}
            aria-label={ariaLabel}
            style={{
              position: "fixed",
              top: position.top,
              left: position.left,
              width: POPOVER_WIDTH,
              maxHeight: POPOVER_MAX_HEIGHT,
              overflowY: "auto",
              background: "white",
              border: "1px solid var(--border)",
              borderRadius: 8,
              boxShadow: "0 8px 24px rgba(16, 40, 70, 0.18)",
              padding: "6px 0",
              zIndex: 3000,
            }}
          >
            {groups.map(({ group, items }, groupIndex) => (
              <div key={group}>
                {groupIndex > 0 && <div style={{ height: 1, background: "var(--border)", margin: "6px 0" }} />}
                <div
                  style={{
                    padding: "8px 14px 4px",
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    color: "var(--text-muted)",
                  }}
                >
                  {GROUP_TITLES[group]}
                </div>
                {items.map((preset) => {
                  const key = getWindowKey(preset);
                  const active = key === valueKey;
                  const primary = formatWindowRange(preset);
                  return (
                    <button
                      key={key}
                      type="button"
                      aria-pressed={active}
                      onKeyDown={onOptionKeyDown}
                      data-preset-option
                      data-preset-key={key}
                      onClick={() => pick(preset)}
                      style={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "8px 14px",
                        border: 0,
                        borderRadius: 0,
                        background: active ? "rgba(0, 123, 255, 0.08)" : "transparent",
                        textAlign: "left",
                        cursor: "pointer",
                        font: "inherit",
                      }}
                    >
                      <span style={{ flex: 1, fontSize: 14, fontWeight: 500, color: "var(--text-dark)" }}>{primary}</span>
                      {active && (
                        <span aria-hidden style={{ color: "var(--primary-blue)", fontWeight: 700 }}>
                          ✓
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>,
          document.body
        )}
    </>
  );
}
